import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Spin } from 'antd';
import moment from 'moment';
import TripHeader from './TripHeader';
import TripProgressSection from './TripProgressSection';
import DriverCheckInCard from './DriverCheckInCard';
import PaymentDetailsCard from './PaymentDetailsCard';
import TripDetailsCard from './TripDetailsCard';
import VehicleDriverCard from './VehicleDriverCard';
import DistanceMetricsCard from './DistanceMetricsCard';
import TimingsCard from './TimingsCard';
import ReplacementHistoryCard from './ReplacementHistoryCard';

function computeScheduleStatus(trackingInfo) {
	if (trackingInfo?.trip_info?.start_date_time && trackingInfo?.trip_timings?.actual_start_time) {
		const startingTime = moment(trackingInfo.trip_info.start_date_time);
		const actualTime = moment(trackingInfo.trip_timings.actual_start_time);
		const diffMins = startingTime.diff(actualTime, 'minutes');
		if (diffMins > 0) return `${Math.abs(diffMins)} mins ahead of schedule`;
		if (diffMins < 0) return `${Math.abs(diffMins)} mins late`;
		return `On time`;
	}
	return trackingInfo?.schedule_status;
}

function buildDistanceData(trackingInfo, scheduleStatusComputed, tripDetails) {
	return {
		final_km: trackingInfo?.final_km ?? null,
		final_source: trackingInfo?.final_source ?? null,
		gps_distance_km: trackingInfo?.accurate_distance_km ?? null,
		odometer_distance_km: trackingInfo?.odometer_distance_km ?? null,
		calculated: trackingInfo?.calculated_distance_km || '-',
		gps_status: trackingInfo?.gps_status || '-',
		last_location_ping: trackingInfo?.last_location_ping || '-',
		schedule_status: scheduleStatusComputed || '-',
		accurate_distance_km: trackingInfo?.accurate_distance_km ?? null,
		planned_distance_km: trackingInfo?.planned_distance_km ?? null,
		virtual_distance_km: trackingInfo?.virtual_distance_km ?? null,
		start_km: tripDetails?.startKm ?? trackingInfo?.trip_info?.start_km ?? null,
		end_km: tripDetails?.endKm ?? trackingInfo?.trip_info?.end_km ?? null,
		is_inter_city: tripDetails?.interCity ?? false,
	};
}

function buildDistanceMetrics(trackingInfo) {
	return {
		planned_distance_km: trackingInfo?.planned_distance_km ?? null,
		odometer_distance_km: trackingInfo?.odometer_distance_km ?? null,
		virtual_distance_km: trackingInfo?.virtual_distance_km ?? null,
		accurate_distance_km: trackingInfo?.accurate_distance_km ?? null,
	};
}

function buildTripData(trackingInfo, liveTracking, scheduleStatusComputed, tripDetails) {
	const tripInfo = trackingInfo?.trip_info;
	return {
		tripInfo,
		nodes: trackingInfo?.tracking_nodes,
		driverCheckIn: trackingInfo?.driver_checkin ? [trackingInfo.driver_checkin] : [],
		payments: {
			payment: trackingInfo?.payment,
			outstanding: trackingInfo?.vendor_rate,
			clientRemaining: trackingInfo?.client_remaining_balance_details || [],
		},
		vehicle: trackingInfo?.vehicle_info,
		isCrossVendor: tripDetails?.is_cross_vendor_vehicle || false,
		vehicleVendorName: tripDetails?.vehicle_vendor_name || '',
		tripHistory: trackingInfo?.adhoc_request_status_history?.data,
		driver:
			trackingInfo?.driver_checkin ||
			(tripInfo?.driver_name
				? {
						driver_name: tripInfo.driver_name,
						driver_number: tripInfo.driver_number || '-',
				  }
				: null),
		trips: trackingInfo?.trips,
		distance: buildDistanceData(trackingInfo, scheduleStatusComputed, tripDetails),
		progress: liveTracking?.trip_progress_percent ?? trackingInfo?.trip_progress_percent ?? 0,
		timings: trackingInfo?.trip_timings,
		distanceMetrics: buildDistanceMetrics(trackingInfo),
		advancePaymentDetail: trackingInfo?.payment?.advance?.amount,
		advacnePaymentStatus: trackingInfo?.payment?.advance?.status,
		clientRate: trackingInfo?.client_rate,
		vendorRate: trackingInfo?.vendor_rate,
		billingType: tripDetails?.billingType,
		clientFixedRatePerDay: tripDetails?.clientFixedRatePerDay,
		vendorFixedRatePerDay: tripDetails?.vendorFixedRatePerDay,
		fixedPerDayBreakdownClient: tripDetails?.fixedPerDayBreakdownClient,
		fixedPerDayBreakdownVendor: tripDetails?.fixedPerDayBreakdownVendor,
		invoiceDetails: {
			...(trackingInfo?.client_invoice_details || {}),
			...(trackingInfo?.invoice_details || {}),
		},
		sourceLocation: tripDetails?.sourceLocation,
		destinationLocation: tripDetails?.destinationLocation,
		source_location: trackingInfo?.source_location,
		destination_location: trackingInfo?.destination_location,
		remarks: tripDetails?.remarks,
		replacementHistory: {
			vehicle:
				trackingInfo?.replacement_history?.vehicle ||
				trackingInfo?.vehicle_replacement_history ||
				[],
			driver:
				trackingInfo?.replacement_history?.driver ||
				trackingInfo?.driver_replacement_history ||
				[],
		},
		tripCode: trackingInfo?.trip_info?.trip_code || tripInfo?.trip_code || '',
	};
}

function TripOverviewTabV2({
	trackingInfo,
	trackingLoading,
	liveTracking,
	headerTripData,
	tripDetails,
	tripId,
}) {
	if (trackingLoading) {
		return (
			<div
				style={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					height: '70vh',
				}}
			>
				<Spin size="large" tip="Loading Trip Details..." />
			</div>
		);
	}
	const scheduleStatusComputed = computeScheduleStatus(trackingInfo);

	const tripData = buildTripData(trackingInfo, liveTracking, scheduleStatusComputed, tripDetails);

	// const isEnded = !!trackingInfo?.trip_info?.trip_status_id;
	const isEnded = trackingInfo?.trip_info?.trip_status_id === 3;
	const isValid =
		(headerTripData?.is_valid ??
			headerTripData?.is_valid_trip ??
			trackingInfo?.trip_info?.is_valid ??
			trackingInfo?.trip_info?.is_valid_trip) !== false;
	const isVendorBilled = trackingInfo?.trip_info?.is_vendor_billed === true;

	const resolvedHeaderTripData = headerTripData || tripData.tripInfo;
	return (
		<div
			style={{ padding: '0px', backgroundColor: '#f5f5f5', minHeight: 'calc(100vh - 200px)' }}
		>
			<TripHeader
				tripData={resolvedHeaderTripData}
				isEnded={isEnded}
				isValid={isValid}
				isVendorBilled={isVendorBilled}
			/>

			<Row gutter={24}>
				<Col span={16}>
					<TripProgressSection
						tripId={tripId || tripData.tripInfo?.id || tripData.tripInfo?.trip_id}
						trackingInfo={tripData.nodes}
						liveTracking={liveTracking}
						tripInfo={tripData.tripInfo}
					/>
					<DriverCheckInCard driverCheckIn={tripData.driverCheckIn} />
					<PaymentDetailsCard
						payments={tripData.payments}
						invoiceDetails={tripData.invoiceDetails}
						clientRate={tripData.clientRate}
					/>
				</Col>

				<Col span={8}>
					<TripDetailsCard tripData={tripData} isValid={isValid} />
					<VehicleDriverCard
						vehicle={tripData.vehicle}
						driver={tripData.driver}
						isCrossVendor={tripData.isCrossVendor}
						vehicleVendorName={tripData.vehicleVendorName}
					/>
					<DistanceMetricsCard
						distance={tripData.distance}
						progress={tripData.progress}
					/>
					<TimingsCard timings={trackingInfo?.trip_timings || {}} />
					<ReplacementHistoryCard
						tripId={tripId || tripData.tripInfo?.id || tripData.tripInfo?.trip_id}
						vehicleReplacements={tripData.replacementHistory.vehicle}
						driverReplacements={tripData.replacementHistory.driver}
						tripCode={tripData.tripCode}
					/>
				</Col>
			</Row>
		</div>
	);
}
TripOverviewTabV2.propTypes = {
	trackingInfo: PropTypes.object,
	trackingLoading: PropTypes.bool,
	liveTracking: PropTypes.object,
	headerTripData: PropTypes.object,
	tripDetails: PropTypes.object,
	tripId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default TripOverviewTabV2;
